import React, { useEffect, useState } from 'react';
import {
  type PeripheralProp,
  type FormikProp,
  type FormikStatus,
} from './types';
import { PAGE_TEXT } from './lang';
import {
  TextField,
  Alert,
  type AlertDetails,
  CountdownButton,
} from '../../../components';
import { useGetEmailVerificationCode } from '../graphql';

export type AccountInfoFormContentProps = {
  formik: FormikProp;
  peripheralProp: PeripheralProp;
};

export const AccountInfoFormContent: React.FunctionComponent<
  AccountInfoFormContentProps
> = (props: AccountInfoFormContentProps) => {
  const {
    formik,
    peripheralProp: { emailUI, sendCodeUI },
  } = props;
  const [openAlert, setOpenAlert] = useState(false);

  const alertDetailDef: AlertDetails = {
    alertContent: '',
    alertType: 'error',
  };
  const [alertDetails, setAlertDetails] =
    useState<AlertDetails>(alertDetailDef);

  const getEmailVerificationCode = useGetEmailVerificationCode();

  const sendVerificationCode = async (): Promise<void> => {
    setButtonLoading(true);
    await getEmailVerificationCode({
      variables: { email: formik.values.email },
    })
      .then(res => {
        return res.data?.sendEmailVerificationCode;
      })
      .then(async json => {
        const { code, message } = json;
        switch (code) {
          case 'EV-C1':
            await formik.setTouched({ email: true });
            formik.setErrors({ email: PAGE_TEXT.EMAIL_FORMAT_ERR });
            break;
          case 'EV-C2':
            await formik.setTouched({ emailVerificationCode: true });
            formik.setErrors({
              emailVerificationCode: PAGE_TEXT.WAIT_FOR_RESEND_V_CODE,
            });
            break;
          case 'EV-C3':
            await formik.setTouched({ emailVerificationCode: true });
            formik.setErrors({
              emailVerificationCode: PAGE_TEXT.SEND_V_CODE_ERR,
            });
            break;
          case 'EV-CS':
            await formik.setTouched({ email: false });
            await formik.setTouched({ emailVerificationCode: false });
            setAlertDetails({
              alertContent: message,
              alertType: 'success',
            });
            setOpenAlert(true);
            setCountdownTime(prev => ({ ...prev, time: 60 }));
            break;
          default:
            throw new Error('Internet Error');
        }
      })
      .catch(err => {
        if (err instanceof Error) {
          setOpenAlert(true);
          setAlertDetails({
            alertContent: `Error Occurs. ${err?.message}`,
            alertType: 'error',
          });
        }
      })
      .finally(() => {
        setButtonLoading(false);
      });
  };

  const formikStatus = formik.status as FormikStatus;

  const [buttonLoading, setButtonLoading] = useState(false);
  const [countdownTime, setCountdownTime] = useState({ time: 0 });

  useEffect(() => {
    const handleDisableSendCode = async (): Promise<void> => {
      sendCodeUI.setDisableSendCode(
        !formik.values.email || emailUI.isUsedEmail(formik.values.email),
      );
    };
    void handleDisableSendCode();
  }, [formik.values.email, emailUI.usedEmail]);

  return (
    <div>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <div style={{ padding: '5px', width: '400px' }}>
        <TextField
          className="w-full"
          required={formikStatus.email}
          label={PAGE_TEXT.EMAIL}
          id="signup-email"
          name="email"
          type="email"
          InputLabelProps={{ shrink: true }}
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
        />
      </div>
      <div style={{ padding: '5px', width: '400px' }} className="flex">
        <div className="w-full">
          <TextField
            className="w-full"
            required={formikStatus.emailVerificationCode}
            label={PAGE_TEXT.EMAIL_VERIFICATION_CODE}
            name="emailVerificationCode"
            type="emailVerificationCode"
            InputLabelProps={{ shrink: true }}
            value={formik.values.emailVerificationCode}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.emailVerificationCode &&
              Boolean(formik.errors.emailVerificationCode)
            }
            helperText={
              formik.touched.emailVerificationCode &&
              formik.errors.emailVerificationCode
            }
          />
        </div>
        <CountdownButton
          onClick={sendVerificationCode}
          className="whitespace-nowrap w-[120px] h-[calc(3.5rem)] !px-5 !ml-2"
          variant="text"
          text={PAGE_TEXT.SEND_EMAIL_VERIFICATION_CODE}
          loading={buttonLoading}
          countdownTime={countdownTime}
          forceDisable={sendCodeUI.canSendCode()}
        />
      </div>
      <div style={{ padding: '5px', width: '400px' }}>
        <TextField
          className="w-full"
          required={formikStatus.password}
          label={PAGE_TEXT.PASSWORD}
          id="signup-password"
          name="password"
          type="password"
          InputLabelProps={{ shrink: true }}
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.password && Boolean(formik.errors.password)}
          helperText={PAGE_TEXT.VALID_PASSWORD}
        />
      </div>
      <div style={{ padding: '5px', width: '400px' }}>
        <TextField
          className="w-full"
          required={formikStatus.confirmPassword}
          label={PAGE_TEXT.CONFIRM_PASSWORD}
          id="signup-confirm-password"
          name="confirmPassword"
          type="password"
          InputLabelProps={{ shrink: true }}
          value={formik.values.confirmPassword}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.confirmPassword &&
            Boolean(formik.errors.confirmPassword)
          }
          helperText={
            formik.touched.confirmPassword && formik.errors.confirmPassword
          }
        />
      </div>
    </div>
  );
};
